import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import QRCode from 'react-qr-code';
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, deleteDoc, getDoc, setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { StarDisplay } from '../components/StarRating';
import { THEMES, buildCustomThemeObject, DEFAULT_CUSTOM_DRAFT } from './Widget';
import {
  LogOut, Star, Check, X, Trash2, Copy, CheckCheck,
  LayoutDashboard, Clock, TrendingUp, Filter, ExternalLink, Code2,
  Pencil, SaveAll, Palette, BookOpen, ChevronDown, ChevronUp,
  Globe, Layout, FileCode, ShoppingBag, Wind, Server, FileText, Share2, Info, Download, Eye,
  MessageSquare, Layers
} from 'lucide-react';

// ─── Sample testimonials for widget preview ───────────────────────────────────
const SAMPLE_TESTIMONIALS = [
  {
    id: 's1', name: 'Sarah M.', rating: 5, status: 'approved',
    message: 'Absolutely incredible service! The team went above and beyond to make sure everything was perfect. I would recommend them to anyone.',
    createdAt: { toDate: () => new Date('2025-03-01') },
  },
  {
    id: 's2', name: 'Daniel B.', rating: 5, status: 'approved',
    message: 'Super easy to work with and the results were amazing. Even my neck pillow customers have been leaving more reviews.',
    createdAt: { toDate: () => new Date('2025-02-20') },
  },
  {
    id: 's3', name: 'Maria L.', rating: 4, status: 'approved',
    message: 'Great experience overall. Really professional and delivered exactly what was promised. Will definitely be back.',
    createdAt: { toDate: () => new Date('2025-02-10') },
  },
  {
    id: 's4', name: 'David K.', rating: 5, status: 'approved',
    message: 'I was blown away by the quality and speed. Honestly one of the best decisions I\'ve made for my business.',
    createdAt: { toDate: () => new Date('2025-01-28') },
  },
];

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
}

// ─── Testimonial card (dashboard) ─────────────────────────────────────────────
function TestimonialCard({ t, onApprove, onReject, onDelete }) {
  const statusColors = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
  };
  const statusLabel = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

  const initials = t.name
    ? t.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const avatarColors = [
    'bg-violet-500', 'bg-indigo-500', 'bg-blue-500',
    'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'
  ];
  const avatarColor = avatarColors[(t.name?.charCodeAt(0) || 0) % avatarColors.length];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${avatarColor} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
            {initials}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
            <p className="text-xs text-gray-400">
              {t.createdAt?.toDate?.()?.toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric'
              }) || 'Unknown date'}
            </p>
          </div>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusColors[t.status]}`}>
          {statusLabel[t.status]}
        </span>
      </div>

      <StarDisplay rating={t.rating} />

      <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
        "{t.message}"
      </p>

      <div className="flex items-center gap-2 pt-1 border-t border-gray-50">
        {t.status !== 'approved' && (
          <button
            onClick={() => onApprove(t.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 text-xs font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition"
          >
            <Check className="w-3.5 h-3.5" /> Approve
          </button>
        )}
        {t.status !== 'rejected' && (
          <button
            onClick={() => onReject(t.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 text-xs font-medium bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition"
          >
            <X className="w-3.5 h-3.5" /> Reject
          </button>
        )}
        <button
          onClick={() => onDelete(t.id)}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
          title="Delete permanently"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Code block ───────────────────────────────────────────────────────────────
function CodeBlock({ code, copyKey, copied, onCopy }) {
  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <span className="text-xs text-gray-400 font-mono">code</span>
        <button
          onClick={() => onCopy(code, copyKey)}
          className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 font-medium transition"
        >
          {copied === copyKey
            ? <><CheckCheck className="w-3.5 h-3.5" /> Copied!</>
            : <><Copy className="w-3.5 h-3.5" /> Copy</>}
        </button>
      </div>
      <pre className="p-4 text-xs overflow-x-auto bg-gray-900 text-green-400 font-mono whitespace-pre-wrap break-all">
        {code}
      </pre>
    </div>
  );
}

// ─── Step ─────────────────────────────────────────────────────────────────────
function Step({ number, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold mt-0.5">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-800 text-sm mb-2">{title}</p>
        {children}
      </div>
    </div>
  );
}

// ─── Platform accordion ───────────────────────────────────────────────────────
function PlatformSection({ icon: Icon, title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <div className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 text-violet-500 flex-shrink-0" />
          <span className="font-medium text-gray-800 text-sm">{title}</span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 py-5 space-y-5 bg-white border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── QR generator ─────────────────────────────────────────────────────────────
function QRGenerator({ url }) {
  const [size, setSize] = useState(200);
  const [fgColor, setFgColor] = useState('#111827');
  const [bgColor, setBgColor] = useState('#ffffff');
  const svgRef = useRef(null);

  function downloadQR() {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const padding = 24;
    canvas.width = size + padding * 2;
    canvas.height = size + padding * 2;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const img = new Image();
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      ctx.drawImage(img, padding, padding, size, size);
      URL.revokeObjectURL(objectUrl);
      const link = document.createElement('a');
      link.download = 'review-qr-code.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = objectUrl;
  }

  const sizes = [
    { label: 'Small', value: 160 },
    { label: 'Medium', value: 200 },
    { label: 'Large', value: 260 },
  ];
  const presets = [
    { label: 'Black', fg: '#111827', bg: '#ffffff' },
    { label: 'White', fg: '#ffffff', bg: '#111827' },
    { label: 'Violet', fg: '#7c3aed', bg: '#ffffff' },
    { label: 'Navy', fg: '#1e3a8a', bg: '#ffffff' },
  ];

  return (
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <div ref={svgRef} className="flex-shrink-0 rounded-xl overflow-hidden p-4 border border-gray-200" style={{ background: bgColor }}>
        <QRCode value={url} size={size} fgColor={fgColor} bgColor={bgColor} level="H" />
      </div>
      <div className="flex-1 space-y-4 min-w-0">
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Size</p>
          <div className="flex gap-2">
            {sizes.map(s => (
              <button key={s.value} onClick={() => setSize(s.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${size === s.value ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Color style</p>
          <div className="flex gap-2 flex-wrap">
            {presets.map(p => (
              <button key={p.label} onClick={() => { setFgColor(p.fg); setBgColor(p.bg); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border ${fgColor === p.fg && bgColor === p.bg ? 'ring-2 ring-violet-500 ring-offset-1 border-violet-300' : 'border-gray-200 hover:border-gray-300'}`}
                style={{ background: p.bg, color: p.fg }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Custom colors</p>
          <div className="flex gap-3 items-center">
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
              <input type="color" value={fgColor} onChange={e => setFgColor(e.target.value)} className="w-7 h-7 rounded cursor-pointer border border-gray-200" />
              Foreground
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
              <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-7 h-7 rounded cursor-pointer border border-gray-200" />
              Background
            </label>
          </div>
        </div>
        <button onClick={downloadQR} className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-xl transition shadow-sm shadow-violet-200">
          <Download className="w-4 h-4" /> Download QR Code (PNG)
        </button>
        <p className="text-xs text-gray-400">High-resolution PNG, ready to print.</p>
      </div>
    </div>
  );
}

// ─── Custom theme builder ─────────────────────────────────────────────────────
function CustomThemeBuilder({ draft, onChange, onSave, saving }) {
  const preview = buildCustomThemeObject(draft);
  const radiusValues = ['sharp', 'rounded', 'pill'];
  const radiusLabels = ['Sharp', 'Rounded', 'Pill'];

  // Sample card for inline live preview
  const sampleCard = {
    name: 'Daniel B.', rating: 5,
    message: 'Absolutely amazing service. My whole store benefits — even the neck pillow section. Would recommend to anyone.',
    createdAt: { toDate: () => new Date('2025-03-01') },
  };
  const initials = 'DB';
  const avatarColor = preview.avatarColors[0];

  return (
    <div className="space-y-6">

      {/* Background */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Background</p>
        <div className="space-y-3">
          <div className="flex gap-2">
            {[
              { id: 'solid', label: 'Solid' },
              { id: 'lr-gradient', label: 'Gradient →' },
              { id: 'diag-gradient', label: 'Gradient ↘' },
            ].map(opt => (
              <button key={opt.id} onClick={() => onChange({ ...draft, bgType: opt.id })}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                  draft.bgType === opt.id
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
              <input type="color" value={draft.bgColor1}
                onChange={e => onChange({ ...draft, bgColor1: e.target.value })}
                className="w-8 h-8 rounded-lg cursor-pointer border border-gray-200" />
              {draft.bgType === 'solid' ? 'Background' : 'Color 1'}
            </label>
            {draft.bgType !== 'solid' && (
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="color" value={draft.bgColor2}
                  onChange={e => onChange({ ...draft, bgColor2: e.target.value })}
                  className="w-8 h-8 rounded-lg cursor-pointer border border-gray-200" />
                Color 2
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Cards</p>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input type="color" value={draft.cardBg}
              onChange={e => onChange({ ...draft, cardBg: e.target.value })}
              className="w-8 h-8 rounded-lg cursor-pointer border border-gray-200" />
            Card background
          </label>
          <div>
            <p className="text-xs text-gray-500 mb-2">
              Corner style: <span className="font-medium text-gray-700">{radiusLabels[radiusValues.indexOf(draft.cardRadius)]}</span>
            </p>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">Sharp</span>
              <input type="range" min={0} max={2} step={1}
                value={radiusValues.indexOf(draft.cardRadius)}
                onChange={e => onChange({ ...draft, cardRadius: radiusValues[+e.target.value] })}
                className="flex-1 accent-violet-600" />
              <span className="text-xs text-gray-400">Pill</span>
            </div>
          </div>
        </div>
      </div>

      {/* Colors */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Colors</p>
        <div className="flex gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input type="color" value={draft.textColor}
              onChange={e => onChange({ ...draft, textColor: e.target.value })}
              className="w-8 h-8 rounded-lg cursor-pointer border border-gray-200" />
            Review text
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input type="color" value={draft.starColor}
              onChange={e => onChange({ ...draft, starColor: e.target.value })}
              className="w-8 h-8 rounded-lg cursor-pointer border border-gray-200" />
            Stars
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input type="color" value={draft.nameColor}
              onChange={e => onChange({ ...draft, nameColor: e.target.value })}
              className="w-8 h-8 rounded-lg cursor-pointer border border-gray-200" />
            Name &amp; headings
          </label>
        </div>
      </div>

      {/* Dark mode toggle */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-700">Dark mode</p>
          <p className="text-xs text-gray-400">Auto-adjusts secondary text, borders, and shadows</p>
        </div>
        <button
          onClick={() => onChange({ ...draft, darkMode: !draft.darkMode })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            draft.darkMode ? 'bg-violet-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            draft.darkMode ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Live preview */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Live preview</p>
        <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
          <div style={{
            background: preview.bg,
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}>
            {/* Mini header */}
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: preview.ratingColor, lineHeight: 1 }}>4.8</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', margin: '4px 0' }}>
                {[1,2,3,4,5].map(s => (
                  <svg key={s} width="14" height="14" viewBox="0 0 24 24"
                    fill={s <= 5 ? preview.starFill : preview.starEmpty} xmlns="http://www.w3.org/2000/svg">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                ))}
              </div>
              <div style={{ fontSize: '11px', color: preview.subColor }}>Based on 12 reviews</div>
            </div>

            {/* Sample card */}
            <div style={{
              background: preview.cardBg,
              borderRadius: preview.cardRadius,
              padding: '16px',
              border: preview.cardBorder,
              boxShadow: preview.cardShadow,
              display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: avatarColor, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '12px',
                }}>{initials}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: preview.nameColor }}>{sampleCard.name}</div>
                  <div style={{ fontSize: '11px', color: preview.dateColor }}>Mar 1, 2025</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[1,2,3,4,5].map(s => (
                  <svg key={s} width="12" height="12" viewBox="0 0 24 24"
                    fill={s <= sampleCard.rating ? preview.starFill : preview.starEmpty} xmlns="http://www.w3.org/2000/svg">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: preview.textColor, lineHeight: '1.6', margin: 0, fontStyle: preview.textStyle }}>
                "{sampleCard.message}"
              </p>
            </div>

            {/* Powered by */}
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <span style={{ fontSize: '10px', color: preview.brandColor }}>Powered by TestimonialWall</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <button onClick={onSave} disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white font-semibold rounded-xl transition shadow-sm shadow-violet-200 text-sm">
        {saving
          ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
          : <><SaveAll className="w-4 h-4" /> Save &amp; apply custom theme</>}
      </button>
    </div>
  );
}

// ─── Widget preview modal ─────────────────────────────────────────────────────
function WidgetPreviewModal({ theme, customTheme, businessName, onClose }) {
  const t = theme === 'custom'
    ? buildCustomThemeObject(customTheme)
    : (THEMES[theme] || THEMES.light);
  const avgRating = (SAMPLE_TESTIMONIALS.reduce((s, x) => s + x.rating, 0) / SAMPLE_TESTIMONIALS.length).toFixed(1);
  const [view, setView] = useState('grid');
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (view !== 'carousel') return;
    const interval = setInterval(() => setCurrent(c => (c + 1) % SAMPLE_TESTIMONIALS.length), 4000);
    return () => clearInterval(interval);
  }, [view]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="bg-white px-5 py-3.5 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
          <div>
            <p className="font-semibold text-gray-900 text-sm">Widget Preview — {t.label} theme</p>
            <p className="text-xs text-gray-400 mt-0.5">This is how your widget looks embedded on a website</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition text-lg font-bold">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto" style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          background: t.bg, padding: '24px 20px', boxSizing: 'border-box',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            {businessName && (
              <div style={{ fontSize: '16px', fontWeight: 700, color: t.headerNameColor, marginBottom: '6px' }}>{businessName}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: t.ratingColor, lineHeight: 1 }}>{avgRating}</div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[1,2,3,4,5].map(s => (
                  <svg key={s} width="18" height="18" viewBox="0 0 24 24" fill={s <= Math.round(avgRating) ? t.starFill : t.starEmpty} xmlns="http://www.w3.org/2000/svg">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                ))}
              </div>
            </div>
            <div style={{ fontSize: '12px', color: t.subColor }}>Based on {SAMPLE_TESTIMONIALS.length} reviews</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
              {['grid', 'carousel'].map(v => (
                <button key={v} onClick={() => setView(v)} style={{
                  padding: '4px 14px', borderRadius: '20px', border: 'none',
                  background: t.toggleBg(view === v), color: t.toggleText(view === v),
                  fontSize: '12px', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize', transition: 'all 0.2s',
                }}>{v}</button>
              ))}
            </div>
          </div>
          {view === 'carousel' ? (
            <div style={{ padding: '0 4px' }}>
              {(() => {
                const card = SAMPLE_TESTIMONIALS[current];
                const color = t.avatarColors[(card.name?.charCodeAt(0) || 0) % t.avatarColors.length];
                const initials = card.name.split(' ').map(n => n[0]).join('').toUpperCase();
                return (
                  <div style={{ background: t.cardBg, borderRadius: t.cardRadius, padding: '20px', border: t.cardBorder, boxShadow: t.cardShadow, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '13px' }}>{initials}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: t.nameColor }}>{card.name}</div>
                        <div style={{ fontSize: '11px', color: t.dateColor }}>{card.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill={s <= card.rating ? t.starFill : t.starEmpty} xmlns="http://www.w3.org/2000/svg">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                      ))}
                    </div>
                    <p style={{ fontSize: '13px', color: t.textColor, lineHeight: '1.65', margin: 0, fontStyle: t.textStyle }}>"{card.message}"</p>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px' }}>
                {SAMPLE_TESTIMONIALS.map((_, i) => (
                  <button key={i} onClick={() => setCurrent(i)} style={{ width: i === current ? '20px' : '8px', height: '8px', borderRadius: '4px', border: 'none', background: i === current ? t.dotActive : t.dotInactive, cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
                ))}
              </div>
            </div>
          ) : (
            <div style={{ columns: '250px', columnGap: '14px' }}>
              {SAMPLE_TESTIMONIALS.map(card => {
                const color = t.avatarColors[(card.name?.charCodeAt(0) || 0) % t.avatarColors.length];
                const initials = card.name.split(' ').map(n => n[0]).join('').toUpperCase();
                return (
                  <div key={card.id} style={{ background: t.cardBg, borderRadius: t.cardRadius, padding: '18px', border: t.cardBorder, boxShadow: t.cardShadow, display: 'flex', flexDirection: 'column', gap: '10px', breakInside: 'avoid', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '12px', flexShrink: 0 }}>{initials}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: t.nameColor }}>{card.name}</div>
                        <div style={{ fontSize: '11px', color: t.dateColor }}>{card.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} width="13" height="13" viewBox="0 0 24 24" fill={s <= card.rating ? t.starFill : t.starEmpty} xmlns="http://www.w3.org/2000/svg">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                      ))}
                    </div>
                    <p style={{ fontSize: '12px', color: t.textColor, lineHeight: '1.6', margin: 0, fontStyle: t.textStyle }}>"{card.message}"</p>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <span style={{ fontSize: '11px', color: t.brandColor }}>Powered by TestimonialWall</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Constants outside component (never recreated) ────────────────────────────
const DEPLOYED_URL = window.location.origin;

const DASHBOARD_TABS = [
  { id: 'Overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'Reviews',  label: 'Reviews',  icon: MessageSquare },
  { id: 'Themes',   label: 'Themes',   icon: Palette },
  { id: 'Install',  label: 'Install',  icon: Code2 },
];

// ─── Dashboard ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Data
  const [testimonials, setTestimonials] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [businessName, setBusinessName] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('light');

  // Custom theme
  const [customDraft, setCustomDraft] = useState(DEFAULT_CUSTOM_DRAFT);
  const [customSaving, setCustomSaving] = useState(false);

  // UI
  const [activeTab, setActiveTab] = useState('Overview');
  const [filter, setFilter] = useState('all');
  const [copied, setCopied] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const businessId = currentUser?.uid;

  // Load business profile
  useEffect(() => {
    if (!businessId) return;
    getDoc(doc(db, 'businesses', businessId)).then(snap => {
      const data = snap.data() || {};
      setBusinessName(data.businessName || '');
      setNameInput(data.businessName || '');
      setSelectedTheme(data.theme || 'light');
      if (data.customTheme) setCustomDraft(data.customTheme);
    });
  }, [businessId]);

  // Live testimonials
  useEffect(() => {
    if (!businessId) return;
    const q = query(collection(db, 'businesses', businessId, 'testimonials'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setTestimonials(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setDataLoading(false);
    });
    return unsub;
  }, [businessId]);

  const handleSaveName = useCallback(async (e) => {
    e.preventDefault();
    setNameSaving(true);
    const trimmed = nameInput.trim();
    await setDoc(doc(db, 'businesses', businessId), { businessName: trimmed }, { merge: true });
    setBusinessName(trimmed);
    setNameEditing(false);
    setNameSaving(false);
  }, [businessId, nameInput]);

  const handleSaveTheme = useCallback(async (key) => {
    setSelectedTheme(key);
    await setDoc(doc(db, 'businesses', businessId), { theme: key }, { merge: true });
  }, [businessId]);

  const handleSaveCustomTheme = useCallback(async () => {
    setCustomSaving(true);
    await setDoc(doc(db, 'businesses', businessId), { theme: 'custom', customTheme: customDraft }, { merge: true });
    setSelectedTheme('custom');
    setCustomSaving(false);
  }, [businessId, customDraft]);

  const handleApprove = useCallback(async (id) => {
    await updateDoc(doc(db, 'businesses', businessId, 'testimonials', id), { status: 'approved' });
  }, [businessId]);

  const handleReject = useCallback(async (id) => {
    await updateDoc(doc(db, 'businesses', businessId, 'testimonials', id), { status: 'rejected' });
  }, [businessId]);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Permanently delete this testimonial?')) {
      await deleteDoc(doc(db, 'businesses', businessId, 'testimonials', id));
    }
  }, [businessId]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const copyToClipboard = useCallback((text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  }, []);

  const stats = useMemo(() => ({
    total: testimonials.length,
    pending: testimonials.filter(t => t.status === 'pending').length,
    approved: testimonials.filter(t => t.status === 'approved').length,
    avgRating: testimonials.length
      ? (testimonials.reduce((s, t) => s + (t.rating || 0), 0) / testimonials.length).toFixed(1)
      : '—',
  }), [testimonials]);

  const filtered = useMemo(
    () => filter === 'all' ? testimonials : testimonials.filter(t => t.status === filter),
    [filter, testimonials]
  );

  const submitUrl = useMemo(() => `${DEPLOYED_URL}/submit/${businessId}`, [businessId]);

  const iframeEmbed = useMemo(() => `<iframe
  src="${DEPLOYED_URL}/widget/${businessId}"
  width="100%"
  height="500"
  frameborder="0"
  style="border:none;border-radius:16px;"
  title="Customer Testimonials"
></iframe>`, [businessId]);

  const jsEmbed = useMemo(() => `<div id="tw-widget"></div>
<script>
  (function(){
    var d=document.getElementById('tw-widget');
    var f=document.createElement('iframe');
    f.src='${DEPLOYED_URL}/widget/${businessId}';
    f.style.cssText='width:100%;height:500px;border:none;border-radius:16px;';
    f.title='Customer Testimonials';
    d.appendChild(f);
  })();
</script>`, [businessId]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Widget preview modal */}
      {previewOpen && (
        <WidgetPreviewModal
          theme={selectedTheme}
          customTheme={customDraft}
          businessName={businessName || 'Your Business'}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* ── Top nav ── */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs tracking-tight">TW</span>
            </div>
            <span className="font-bold text-gray-900">TestimonialWall</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={submitUrl} target="_blank" rel="noreferrer"
              className="hidden sm:flex items-center gap-1.5 text-sm text-gray-500 hover:text-violet-600 transition">
              <ExternalLink className="w-4 h-4" /> Review Form
            </a>
            <div className="w-px h-5 bg-gray-200" />
            <span className="text-sm text-gray-500 hidden sm:block truncate max-w-[160px]">{currentUser?.email}</span>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex border-t border-gray-100 overflow-x-auto">
          {DASHBOARD_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                activeTab === tab.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'Reviews' && stats.pending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                  {stats.pending}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      {/* ── Tab content ── */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8">

        {/* ════════ OVERVIEW ════════ */}
        {activeTab === 'Overview' && (
          <div className="space-y-8">
            {/* Welcome banner — only shown when no reviews yet */}
            {!dataLoading && testimonials.length === 0 && (
              <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl border border-violet-100 p-6 sm:p-8">
                <div className="mb-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Welcome to TestimonialWall 👋</h2>
                  <p className="text-sm text-gray-500">
                    TestimonialWall lets you collect real reviews from your customers and display them as a beautiful widget on your website — no coding required. Here's how it works:
                  </p>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 border border-violet-100 shadow-sm">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center mb-3">
                      <span className="text-violet-700 font-black text-sm">1</span>
                    </div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Share your review link</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Send your unique link or QR code to customers — by text, email, or print it on a receipt. They fill out a quick form and submit.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-violet-100 shadow-sm">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center mb-3">
                      <span className="text-violet-700 font-black text-sm">2</span>
                    </div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Approve the reviews you want</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Reviews show up in the Reviews tab. Approve the good ones — only approved reviews show on your website.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-violet-100 shadow-sm">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center mb-3">
                      <span className="text-violet-700 font-black text-sm">3</span>
                    </div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Embed it on your website</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Copy one line of code from the Install tab and paste it into your site. Reviews appear automatically — no updates needed.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <a href={submitUrl} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition shadow-sm shadow-violet-200">
                    <ExternalLink className="w-4 h-4" /> Open your review form
                  </a>
                  <p className="text-xs text-gray-400">↑ Share this link with your first customer to get started</p>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Layers} label="Total" value={stats.total} color="bg-violet-500" />
              <StatCard icon={Clock} label="Pending" value={stats.pending} color="bg-amber-500" />
              <StatCard icon={Check} label="Approved" value={stats.approved} color="bg-emerald-500" />
              <StatCard icon={TrendingUp} label="Avg Rating" value={stats.avgRating} color="bg-blue-500" />
            </div>

            {/* Business name */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Pencil className="w-5 h-5 text-violet-500" />
                <h2 className="font-semibold text-gray-900">Business name</h2>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Your business name appears on the customer submission form and at the top of your embedded widget.
              </p>
              {nameEditing ? (
                <form onSubmit={handleSaveName} className="flex gap-2">
                  <input
                    type="text" value={nameInput} onChange={e => setNameInput(e.target.value)}
                    maxLength={60} required autoFocus placeholder="e.g. Acme Plumbing Co."
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-gray-900 placeholder-gray-400 transition text-sm"
                  />
                  <button type="submit" disabled={nameSaving}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white font-medium rounded-xl transition text-sm">
                    {nameSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><SaveAll className="w-4 h-4" /> Save</>}
                  </button>
                  <button type="button" onClick={() => { setNameEditing(false); setNameInput(businessName); }}
                    className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl transition">
                    Cancel
                  </button>
                </form>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${businessName ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                    {businessName || 'No business name set yet'}
                  </span>
                  <button onClick={() => setNameEditing(true)}
                    className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium transition">
                    <Pencil className="w-3.5 h-3.5" />
                    {businessName ? 'Edit' : 'Add name'}
                  </button>
                </div>
              )}
            </div>

            {/* Quick links */}
            <div className="grid sm:grid-cols-3 gap-4">
              <a href={submitUrl} target="_blank" rel="noreferrer"
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-violet-200 transition group flex items-center gap-4">
                <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center group-hover:bg-violet-200 transition">
                  <ExternalLink className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Review form</p>
                  <p className="text-xs text-gray-400">Share with customers</p>
                </div>
              </a>
              <button onClick={() => setActiveTab('Reviews')}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-violet-200 transition group flex items-center gap-4 text-left">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Reviews</p>
                  <p className="text-xs text-gray-400">{stats.pending > 0 ? `${stats.pending} pending approval` : `${stats.approved} approved`}</p>
                </div>
              </button>
              <button onClick={() => setActiveTab('Install')}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-violet-200 transition group flex items-center gap-4 text-left">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition">
                  <Code2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Install widget</p>
                  <p className="text-xs text-gray-400">Add to your website</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ════════ REVIEWS ════════ */}
        {activeTab === 'Reviews' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Reviews</h2>
                <p className="text-sm text-gray-500 mt-0.5">Approve reviews to make them show on your website</p>
              </div>
              <a href={submitUrl} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-xl transition shadow-sm shadow-violet-200">
                <ExternalLink className="w-4 h-4" /> Open review form
              </a>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-gray-400" />
              {[
                { key: 'all',      label: `All (${stats.total})` },
                { key: 'pending',  label: `Pending (${stats.pending})` },
                { key: 'approved', label: `Approved (${stats.approved})` },
                { key: 'rejected', label: 'Rejected' },
              ].map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    filter === f.key
                      ? 'bg-violet-600 text-white shadow-sm shadow-violet-200'
                      : 'bg-white text-gray-500 border border-gray-200 hover:border-violet-300'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>

            {dataLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <Star className="w-10 h-10 text-gray-200 fill-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                  {filter === 'all' ? 'No reviews yet' : `No ${filter} reviews`}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {filter === 'all' ? 'Share your review form link to start collecting reviews.' : `Switch to "All" to see every review.`}
                </p>
                {filter === 'all' && (
                  <a href={submitUrl} target="_blank" rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-xl transition">
                    <ExternalLink className="w-4 h-4" /> Open Your Review Form
                  </a>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map(t => (
                  <TestimonialCard key={t.id} t={t} onApprove={handleApprove} onReject={handleReject} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════════ THEMES ════════ */}
        {activeTab === 'Themes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Widget theme</h2>
                <p className="text-sm text-gray-500 mt-0.5">Pick a look for your embedded widget. Changes go live instantly.</p>
              </div>
              <button onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-700 rounded-xl text-sm font-semibold transition">
                <Eye className="w-4 h-4" /> Preview widget
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.values(THEMES).map(t => (
                  <button key={t.key} onClick={() => handleSaveTheme(t.key)}
                    className={`relative rounded-xl overflow-hidden transition-all text-left ${
                      selectedTheme === t.key
                        ? 'ring-2 ring-violet-500 ring-offset-2 shadow-md'
                        : 'ring-1 ring-gray-200 hover:ring-gray-300'
                    }`}>
                    <div style={{ background: t.previewBg }} className="h-14 w-full flex items-end justify-center pb-1 gap-1 px-2">
                      {[40, 32, 36].map((h, i) => (
                        <div key={i} style={{ background: t.previewCard, border: t.previewBorder, borderRadius: '4px', width: '22px', height: `${h}px`, flexShrink: 0 }} />
                      ))}
                    </div>
                    <div className={`py-1.5 px-2 flex items-center justify-between ${selectedTheme === t.key ? 'bg-violet-50' : 'bg-gray-50'}`}>
                      <span className={`text-xs font-medium truncate ${selectedTheme === t.key ? 'text-violet-700' : 'text-gray-600'}`}>{t.label}</span>
                      {t.dynamic && <span className="text-xs text-violet-400 ml-1" title="Mouse-reactive">✦</span>}
                    </div>
                    {selectedTheme === t.key && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-violet-500 rounded-full flex items-center justify-center">
                        <svg width="8" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                    )}
                  </button>
                ))}

                {/* Custom theme card */}
                <button
                  onClick={() => document.getElementById('custom-theme-builder')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`relative rounded-xl overflow-hidden transition-all text-left ${
                    selectedTheme === 'custom'
                      ? 'ring-2 ring-violet-500 ring-offset-2 shadow-md'
                      : 'ring-1 ring-gray-200 hover:ring-gray-300'
                  }`}>
                  <div className="h-14 w-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f59e0b 100%)' }}>
                    <Palette className="w-5 h-5 text-white opacity-90" />
                  </div>
                  <div className={`py-1.5 px-2 ${selectedTheme === 'custom' ? 'bg-violet-50' : 'bg-gray-50'}`}>
                    <span className={`text-xs font-medium truncate ${selectedTheme === 'custom' ? 'text-violet-700' : 'text-gray-600'}`}>Custom ✏️</span>
                  </div>
                  {selectedTheme === 'custom' && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-violet-500 rounded-full flex items-center justify-center">
                      <svg width="8" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-4 flex items-center gap-3 flex-wrap">
                <button onClick={() => setPreviewOpen(true)} className="text-violet-500 hover:underline">
                  Preview with sample reviews →
                </button>
                <span className="text-gray-300">|</span>
                <a href={`/widget/${businessId}`} target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">
                  Open live widget →
                </a>
                <span className="text-amber-500">✦ = mouse-reactive theme</span>
              </p>
            </div>

            {/* Custom theme builder */}
            <div id="custom-theme-builder" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-1">
                <Palette className="w-5 h-5 text-violet-500" />
                <h3 className="font-semibold text-gray-900">Build your own theme</h3>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Design a custom look with your brand colors. Click <strong>Save &amp; apply</strong> to activate it.
              </p>
              <CustomThemeBuilder
                draft={customDraft}
                onChange={setCustomDraft}
                onSave={handleSaveCustomTheme}
                saving={customSaving}
              />
            </div>
          </div>
        )}

        {/* ════════ INSTALL ════════ */}
        {activeTab === 'Install' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add to your website</h2>
              <p className="text-sm text-gray-500 mt-0.5">Your widget and submission form are ready. Follow the steps below.</p>
            </div>

            {/* 3 steps summary */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-violet-50 rounded-xl p-4">
                <div className="text-2xl font-black text-violet-200 mb-2">01</div>
                <p className="font-semibold text-violet-800 text-sm mb-1">Copy your embed code</p>
                <p className="text-xs text-violet-600">Choose iFrame or JavaScript below and copy it.</p>
              </div>
              <div className="bg-violet-50 rounded-xl p-4">
                <div className="text-2xl font-black text-violet-200 mb-2">02</div>
                <p className="font-semibold text-violet-800 text-sm mb-1">Paste into your site</p>
                <p className="text-xs text-violet-600">Add an "HTML" block on Wix, Squarespace, WordPress, etc. and paste the code.</p>
              </div>
              <div className="bg-violet-50 rounded-xl p-4">
                <div className="text-2xl font-black text-violet-200 mb-2">03</div>
                <p className="font-semibold text-violet-800 text-sm mb-1">Share your review link</p>
                <p className="text-xs text-violet-600">Send customers your link or QR code. New reviews appear automatically.</p>
              </div>
            </div>

            {/* Embed codes */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Code2 className="w-5 h-5 text-violet-500" />
                <h3 className="font-semibold text-gray-900">Your embed codes</h3>
              </div>
              <p className="text-xs text-gray-500">
                Paste either snippet into your website where you want reviews to appear. You only need to do this <strong className="text-gray-700">once</strong>.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">✅ Option A — iFrame (recommended)</p>
                  <CodeBlock code={iframeEmbed} copyKey="iframe" copied={copied} onCopy={copyToClipboard} />
                  <p className="text-xs text-gray-400 mt-1.5">Works on Wix, Squarespace, Shopify, WordPress, GoDaddy, plain HTML</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">Option B — JavaScript</p>
                  <CodeBlock code={jsEmbed} copyKey="js" copied={copied} onCopy={copyToClipboard} />
                  <p className="text-xs text-gray-400 mt-1.5">Use this if the iFrame option doesn't display correctly</p>
                </div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <p className="text-xs text-blue-700">
                  <strong>📋 Customer submission form:</strong>{' '}
                  <a href={submitUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{submitUrl}</a>
                  {'  '}
                  <button onClick={() => copyToClipboard(submitUrl, 'submitUrl')} className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 font-medium transition ml-1">
                    {copied === 'submitUrl' ? <><CheckCheck className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                  <br /><span className="text-blue-600">Share this link with customers so they can leave reviews.</span>
                </p>
              </div>
            </div>

            {/* Platform guides */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-violet-500" />
                <h3 className="font-semibold text-gray-900">Step-by-step guides for your platform</h3>
              </div>
              <p className="text-xs text-gray-400">Click your website platform below for detailed instructions.</p>
              <div className="space-y-2">

                <PlatformSection icon={Globe} title="Wix">
                  <Step number="1" title="Open the Wix Editor">Log in at <span className="font-mono text-xs bg-gray-100 px-1 rounded">wix.com</span> → click <strong>Edit Site</strong>.</Step>
                  <Step number="2" title="Add an HTML Embed block">Left panel → blue <strong>+</strong> → <strong>Embed Code</strong> → <strong>Embed HTML</strong>. Drag the box where you want reviews.</Step>
                  <Step number="3" title="Paste your embed code">Click the box → <strong>Enter Code</strong> → paste the iFrame code → <strong>Apply</strong>.</Step>
                  <Step number="4" title="Resize and publish">Drag the box taller if needed (500–600px is ideal). Click <strong>Publish</strong>. Done!</Step>
                </PlatformSection>

                <PlatformSection icon={Layout} title="Squarespace">
                  <Step number="1" title="Open the page editor">Log in → <strong>Pages</strong> → click the page → <strong>Edit</strong>.</Step>
                  <Step number="2" title="Add a Code block">Click <strong>+</strong> where you want the widget → search <strong>Code</strong> → click it.</Step>
                  <Step number="3" title="Paste and save">Set mode to <strong>HTML</strong>. Paste the iFrame code → <strong>Apply</strong> → <strong>Save</strong>.</Step>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>If you see a blank box, try the <strong>JavaScript snippet</strong> instead — some Squarespace plans block iFrames.</span>
                  </div>
                </PlatformSection>

                <PlatformSection icon={FileCode} title="WordPress">
                  <Step number="1" title="Open the page">WordPress admin → <strong>Pages</strong> → <strong>Edit</strong>.</Step>
                  <Step number="2" title="Add a Custom HTML block">Click <strong>+</strong> → search <strong>Custom HTML</strong> → click it.</Step>
                  <Step number="3" title="Paste and update">Paste the iFrame code inside the block → click <strong>Update</strong>.</Step>
                  <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>Using Classic editor? Switch to <strong>Text</strong> mode (not Visual) before pasting.</span>
                  </div>
                </PlatformSection>

                <PlatformSection icon={ShoppingBag} title="Shopify">
                  <Step number="1" title="Go to theme editor">Shopify admin → <strong>Online Store</strong> → <strong>Themes</strong> → <strong>Customize</strong>.</Step>
                  <Step number="2" title="Navigate to the page">Use the page dropdown to find where you want reviews.</Step>
                  <Step number="3" title="Add a Custom Liquid section">Click <strong>Add section</strong> → <strong>Custom Liquid</strong> → paste the iFrame code → <strong>Save</strong>.</Step>
                </PlatformSection>

                <PlatformSection icon={Wind} title="Webflow">
                  <Step number="1" title="Open the Designer">Log in → open project → <strong>Designer</strong>.</Step>
                  <Step number="2" title="Add an Embed element">Left panel → <strong>+</strong> → <strong>Components</strong> → drag an <strong>Embed</strong> block onto canvas.</Step>
                  <Step number="3" title="Paste and publish">Double-click the Embed block → paste the iFrame code → <strong>Save & Close</strong> → <strong>Publish</strong>.</Step>
                </PlatformSection>

                <PlatformSection icon={Server} title="GoDaddy Website Builder">
                  <Step number="1" title="Open the editor">Log in → <strong>My Products</strong> → <strong>Edit Website</strong>.</Step>
                  <Step number="2" title="Add an HTML section">Click <strong>+</strong> → scroll down → choose <strong>HTML</strong>.</Step>
                  <Step number="3" title="Paste and publish">Click the section → edit icon → paste iFrame code → <strong>Done</strong> → <strong>Publish</strong>.</Step>
                </PlatformSection>

                <PlatformSection icon={FileText} title="Plain HTML / Custom website">
                  <Step number="1" title="Open your HTML file">Open the page file (e.g. <span className="font-mono text-xs bg-gray-100 px-1 rounded">index.html</span>) in VS Code or any editor.</Step>
                  <Step number="2" title="Paste the code">Find where you want reviews and paste the iFrame code directly inside a <span className="font-mono text-xs bg-gray-100 px-1 rounded">&lt;section&gt;</span> or <span className="font-mono text-xs bg-gray-100 px-1 rounded">&lt;div&gt;</span>.</Step>
                  <Step number="3" title="Save and upload">Save the file and re-upload it to your web host. Widget appears immediately.</Step>
                </PlatformSection>

                <PlatformSection icon={Share2} title="Share via Email / Text / QR code">
                  <Step number="1" title="Copy your submission form link">
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">{submitUrl}</span>
                      <button onClick={() => copyToClipboard(submitUrl, 'submitUrl2')}
                        className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium transition flex-shrink-0">
                        {copied === 'submitUrl2' ? <><CheckCheck className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                      </button>
                    </div>
                  </Step>
                  <Step number="2" title="Send via email or text">
                    Paste the link into a text or email: <em>"Hi [Name], could you leave us a quick review? Takes 30 seconds! [link]"</em>
                  </Step>
                  <Step number="3" title="Download your QR code">
                    <p className="text-xs text-gray-500 mb-4">Print on receipts, business cards, or in-store signs so customers can scan and review instantly.</p>
                    <QRGenerator url={submitUrl} />
                  </Step>
                </PlatformSection>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
